import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import ApiService from '@/services/api.service';
import { useApiAuthReady } from '@/hooks/useApiAuthReady';
import { githubCallbackStep, REAUTHORIZE_GUARD_KEY } from './githubCallbackStep';

/**
 * Where GitHub sends the browser after the Pliable App is installed or authorized.
 *
 * The handshake has two legs and GitHub uses one URL for both, so this page has to
 * tell them apart:
 *
 *   install   → `?installation_id=…&setup_action=install`. The App requests OAuth
 *               during installation, so this usually carries a `code` too — and a
 *               `state`, when the install started from `/github/install-url`.
 *   authorize → `?code=…&state=…`, from the OAuth leg this page starts itself.
 *
 * A `code` with our `state` is exchanged server-side purely to confirm the
 * installation really belongs to this user — otherwise anyone could post someone
 * else's installation id and bind a repo they don't own. Anything else (no code,
 * or a code with no state) holds the installation id and runs the OAuth leg; see
 * githubCallbackStep for why a stateless code is dropped rather than posted.
 *
 * The installation id is stashed in localStorage between the legs because GitHub
 * does not carry it through the OAuth redirect.
 *
 * This path must match the App's **Setup URL** and **Callback URL**. It is the same
 * path the old frontend used, so an App configured for that keeps working.
 */
const INSTALL_ID_KEY = '_gh_install_id';

/**
 * Returning from github.com is a direct page load, which is exactly the case
 * `useApiAuthReady` exists for: the auth context has a user a beat before
 * AuthService — the singleton ApiService reads to build the header — has the
 * token. Child effects run before parent effects, so exchanging the code on mount
 * sent no Authorization header and the API answered 401.
 */
const AUTH_GIVE_UP_MS = 8000;

type Phase = 'working' | 'done' | 'error';

export default function GithubAppCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const authReady = useApiAuthReady();
  const [phase, setPhase] = useState<Phase>('working');
  const [message, setMessage] = useState('Finishing the GitHub connection…');
  // StrictMode double-invokes effects in dev; the code is single-use, so a second
  // exchange would fail and show a spurious error.
  const ran = useRef(false);

  // The hook stops polling after a few seconds rather than hanging. Say so instead
  // of spinning forever.
  useEffect(() => {
    if (authReady) return;
    const id = setTimeout(() => {
      if (ran.current) return;
      setPhase('error');
      setMessage(
        'Your session was not ready in time to finish connecting GitHub. Reload this page to try again.',
      );
    }, AUTH_GIVE_UP_MS);
    return () => clearTimeout(id);
  }, [authReady, params]);

  useEffect(() => {
    if (ran.current) return;

    const installationId = params.get('installation_id');

    // Both legs are authenticated now. Starting the OAuth leg used to be a plain
    // redirect to a public endpoint, which is exactly why it could not carry a
    // state tied to anyone — see src/services/git/oauth_state.py.
    if (!authReady) return;
    ran.current = true;

    if (installationId) {
      try {
        localStorage.setItem(INSTALL_ID_KEY, installationId);
      } catch {
        // Private windows and blocked site data — the OAuth leg below can still
        // succeed if GitHub happens to send the id back with the code.
      }
    }

    let lastReauthorizeAt: number | null = null;
    try {
      const raw = sessionStorage.getItem(REAUTHORIZE_GUARD_KEY);
      lastReauthorizeAt = raw ? Number(raw) : null;
    } catch {
      lastReauthorizeAt = null;
    }
    const step = githubCallbackStep(params, lastReauthorizeAt, Date.now());

    if (step.kind === 'loop') {
      setPhase('error');
      setMessage(
        'GitHub keeps returning without confirming this session. Start the install again from Settings → Git.',
      );
      return;
    }

    if (step.kind === 'authorize') {
      // Install leg, or a code we can't prove is ours. Go get one we can.
      if (params.get('code')) {
        try {
          sessionStorage.setItem(REAUTHORIZE_GUARD_KEY, String(Date.now()));
        } catch {
          // Without the guard a looping GitHub would loop here; it never has.
        }
      }
      setMessage('Confirming your GitHub identity…');
      ApiService.getInstance()
        .request('POST', '/github/authorize-url')
        .then((res) => {
          window.location.href = (res as { url: string }).url;
        })
        .catch((err: unknown) => {
          setPhase('error');
          setMessage(
            err instanceof Error ? err.message : 'Could not start the GitHub authorization.',
          );
        });
      return;
    }

    try {
      sessionStorage.removeItem(REAUTHORIZE_GUARD_KEY);
    } catch {
      /* nothing to clean up */
    }

    let storedId: string | null = null;
    try {
      storedId = localStorage.getItem(INSTALL_ID_KEY);
    } catch {
      storedId = null;
    }
    const resolvedId = installationId || storedId;

    if (!resolvedId) {
      setPhase('error');
      setMessage(
        "We couldn't tell which GitHub installation this was. Start the install again from Settings → Git.",
      );
      return;
    }

    ApiService.getInstance()
      // `state` is what proves this authorization was started from this session
      // and not handed to the user in a link. The server refuses without it.
      .request('POST', '/github/verify', {
        code: step.code,
        installation_id: resolvedId,
        state: step.state,
      })
      .then(() => {
        try {
          localStorage.removeItem(INSTALL_ID_KEY);
        } catch {
          /* nothing to clean up */
        }
        setPhase('done');
        setMessage('GitHub connected. Taking you back to settings…');
        setTimeout(() => navigate('/settings?tab=git', { replace: true }), 900);
      })
      .catch((err: unknown) => {
        setPhase('error');
        // Surface what the server actually said. "API Error" alone left no way to
        // tell a session problem from a rejected installation id.
        const detail =
          (err as { payload?: { msg?: string; description?: string } })?.payload?.msg ??
          (err as { payload?: { description?: string } })?.payload?.description ??
          (err instanceof Error ? err.message : String(err));
        setMessage(detail);
      });
  }, [params, navigate, authReady]);

  return (
    <div className="mx-auto max-w-lg px-4 py-16">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {phase === 'working' && <Loader2 className="h-4 w-4 animate-spin" />}
            {phase === 'done' && <CheckCircle2 className="h-4 w-4 text-green-600" />}
            {phase === 'error' && <AlertCircle className="h-4 w-4 text-destructive" />}
            Connecting GitHub
          </CardTitle>
          <CardDescription>Linking the Pliable GitHub App to this tenant.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {phase === 'error' ? (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{message}</AlertDescription>
            </Alert>
          ) : (
            <p className="text-sm text-muted-foreground">{message}</p>
          )}
          {phase === 'error' && (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                If this was a session problem, reloading will retry. GitHub's code is single-use,
                so if it had already been spent you'll need to start the install again from Git
                settings.
              </p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => window.location.reload()}>
                  Retry
                </Button>
                <Button onClick={() => navigate('/settings?tab=git', { replace: true })}>
                  Back to Git settings
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
